import React from "react";
import { Col, Select, Space } from "antd";
import InputNumber from "../../InputNumber";
import FormItem from "../../utils/FormItem";
import { Controller, useFormContext } from "react-hook-form";
import { CampaignPriceModel, CampaignRateCard, CampaignType, CompanyType } from '../../../../__generated__/graphql';
import { useCurrentUser } from "../../../../utils/CurrentUserContext";

type Option = {
  label: string;
  value: CampaignPriceModel;
}

const CPM = {label: 'CPM', value: CampaignPriceModel.Cpm}
const CPR = {label: 'Cost per read', value: CampaignPriceModel.Cpr}
const CPC = {label: 'Cost per click', value: CampaignPriceModel.Cpc}
const CPA = {label: 'Cost per conversion', value: CampaignPriceModel.Cpa}
const CPCLO = {label: 'Cost per clickout', value: CampaignPriceModel.Cpclo}
const ALL_OPTIONS = [CPR, CPCLO, CPC, CPM, CPA]

// Returns price model options based on campaign type and company settings
const getOptionsForCampaignType = (campaignType: CampaignType, companyType: CompanyType, allowMarketplaceCpa: boolean): Option[] => {
  const baseOptionsForAgencyAndAdvertiser: Record<CampaignType, Option[]> = {
    [CampaignType.Filler]: ALL_OPTIONS,
    [CampaignType.InHouseNativeDisplay]: [],
    [CampaignType.InHouse]: [],
    [CampaignType.MarketplaceNativeDisplay]: [CPC],
    [CampaignType.Standard]: [CPR],
    [CampaignType.ContentRecirculation]: ALL_OPTIONS,
  }

  const baseOptionsForPublisher: Record<CampaignType, Option[]> = {
    [CampaignType.Filler]: [],
    [CampaignType.InHouseNativeDisplay]: [CPC, CPM, CPA],
    [CampaignType.InHouse]: ALL_OPTIONS,
    [CampaignType.MarketplaceNativeDisplay]: [CPC],
    [CampaignType.Standard]: [CPR],
    [CampaignType.ContentRecirculation]: ALL_OPTIONS,
  }

  const baseOptionsForNone: Record<CampaignType, Option[]> = {
    [CampaignType.Filler]: [],
    [CampaignType.InHouseNativeDisplay]: [],
    [CampaignType.InHouse]: [],
    [CampaignType.MarketplaceNativeDisplay]: [],
    [CampaignType.Standard]: [],
    [CampaignType.ContentRecirculation]: ALL_OPTIONS,
  }

  let options: Option[] = []

  if (companyType === CompanyType.Publisher) {
    options = baseOptionsForPublisher[campaignType] || []
  } else if (companyType === CompanyType.Agency || companyType === CompanyType.Advertiser) {
    options = baseOptionsForAgencyAndAdvertiser[campaignType] || []
  } else {
    options = baseOptionsForNone[campaignType] || []
  }

  // Add CPA to STANDARD and NATIVE_DISPLAY_MARKETPLACE campaigns if allowMarketplaceCpa is true
  if (allowMarketplaceCpa &&
      (campaignType === CampaignType.Standard || campaignType === CampaignType.MarketplaceNativeDisplay) &&
      !options.find(opt => opt.value === CampaignPriceModel.Cpa)) {
    options = [...options, CPA]
  }

  return options
}

const CampaignRateCardField = () => {
  const form = useFormContext();
  const { watch } = form

  const {user} = useCurrentUser()
  const campaignType: CampaignType = watch('type')
  const label = campaignType === CampaignType.InHouse ? 'Price' : 'Bid'
  const rules = { validate: (value: CampaignRateCard) => value && ![undefined, null, ''].includes(value.price)}

  const options = getOptionsForCampaignType(campaignType, user.company.type, user.company.allowMarketplaceCpa || false)
  const rateCard = watch('rateCard') as CampaignRateCard | undefined
  const hasValue = !!rateCard?.priceModel && rateCard?.price !== undefined && rateCard?.price !== null && rateCard?.price !== ''

  const showRequired = !hasValue || !!form.formState.errors?.rateCard

  return (
    <Col span={12}>    
      <FormItem label={label} top required={showRequired}>
        <Space.Compact className="max-w-xs w-full campaignRateCardField">
          <Controller
            control={form.control}
            name="rateCard"
            rules={rules}
            render={({ field: {value, onChange} }) => (
              <>
                <Select
                  className="rounded-r-none"
                  style={{ width: "60%" }}
                  size="large"
                  value={value?.priceModel}
                  onChange={(priceModel) => onChange({...value, priceModel})}
                  options={options}
                />

                <InputNumber
                  value={value?.price}
                  min={0}
                  onChange={(price) => onChange({ ...value, price })}
                  className="py-[3px] rounded-l-none"
                  style={{ width: "40%" }}
                  suffix='DKK'
                />
              </>
            )}
          />
        </Space.Compact>
      </FormItem>
    </Col>
  );
};

export default CampaignRateCardField;
